﻿import React, { useState, useEffect } from 'react';
import { ResidueOnIgnitionBenchSheet } from '../models/residue-on-ignition-bench-sheet';
import { DocumentationConstants } from '../store/documentation-constants';
import BenchSheetToolbar from './BenchSheetToolbar';
import { BenchSheetTechnologyType, DocumentType, Entities, NoteType } from '../models/enums';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import { LookupItem } from '../models/lookup-item';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetWeightData from './BenchSheetWeightData';
import ResidueOnIgnitionInstruments from './ResidueOnIgnitionInstruments';
import BenchSheetData from './BenchSheetData';
import NoteViewer from './NoteViewer';
import { CrudFunctions } from '../utilities/crud-functions';
import StandardsViewer from './StandardsViewer';
import TicketLogEntryForm from './TicketLogEntryForm';
import GlobalDialog from './Modal/GlobalDialog';
import { FieldUpdate } from '../models/field-update';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { UserDataStore } from '../store/userDataStore';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';

//temp
const acids: LookupItem[] = [
    { id: 1, name: 'Sulfuric Acid' },
    { id: 2, name: 'Nitric Acid' }
];

let key = 1;

export default function ResidueOnIgitionBenchSheetForm() {
    const [benchSheet, setBenchSheet] =
        useState<ResidueOnIgnitionBenchSheet>();

    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>(false);

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];
    const technologyId = BenchSheetTechnologyType.ResidueOnIgnition;

    key++;

    return (
        <div className="pw-content">
            <div className="pw-container">

                <BenchSheetToolbar title="ROI Bench Sheet"
                    subtitle="Residue on Ignition"
                    auditRowPointer={benchSheetId} entity={Entities.RoiBenchSheets}
                    technologyId={BenchSheetTechnologyType.ResidueOnIgnition}
                    documentationId={DocumentationConstants.ROIBenchSheet}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                {benchSheet &&
                    <ResidueOnIgnitionInstruments benchSheet={benchSheet} readOnly={!canEdit} />
                }

                <div className='pw-comp-row'>
                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Acid</div>
                        <SelectBox id="acidId" className="selectbox"
                            placeholder='NA' readOnly={!canEdit}
                            displayExpr='name' valueExpr='id'
                            dataSource={acids ? acids : []}
                            onValueChanged={onAcidSelectionChanged}
                            value={benchSheet && benchSheet.acidId ?
                                benchSheet.acidId : null} />
                    </div>
                </div>

                <div className="pw-margin-top-1">
                    <BenchSheetWeightData benchSheetId={benchSheetId}
                        technologyId={technologyId} isReadOnly={!canEdit}
                        datagridHeight="20rem" title="ROI Weight Data" />
                </div>

                <div className="pw-margin-top-1">
                    <NoteViewer height='10rem' title="Bench Sheet Note"
                        noteType={NoteType.Method} parentId={benchSheetId} />
                </div>

                <div className="pw-margin-top-1">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={technologyId}
                        hideImageHandler={true}
                        documentType={DocumentType.General} />
                </div>

                {/*
                <div>
                    <StandardsViewer batchId={benchSheet && benchSheet.batchId ?
                        benchSheet.batchId : undefined}
                        technologyId={technologyId} />
                </div>
                */}
                <DmaFooter />

            </div>

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="ROI Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function onAcidSelectionChanged(e: any) {
        if (!e.event)
            return;

        if (benchSheet) {
            const caller = e.element.id as string;
            const currentValue = benchSheet[caller];

            setBenchSheet({ ...benchSheet, acidId: e.value });

            let fieldUpdate: FieldUpdate = {
                rowId: benchSheetId,
                entity: "ResidueOnIgnitionBenchSheets",
                field: "acidId",
                value: e.value
            };

            await updateRoibenchSheetField(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Bench Sheet");
                });
        }
    }

    async function getAllData() {
        await getBenchSheet();
    }

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function updateRoibenchSheetField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchsheets/roibenchsheetfield", fieldUpdate);
    }

    //show/hide
    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }


}

